"use client";

import { useEffect, useMemo, useState } from "react";
import {
  Timestamp,
  addDoc,
  collection,
  deleteDoc,
  doc,
  updateDoc,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useTranslations } from "@/contexts/I18nContext";
import { Avatar } from "@/components/ui/Avatar";
import {
  buildResponsibilityDoc,
  type RecurrenceValue,
} from "@/lib/createResponsibility";
import type { MemberProfile, Responsibility } from "@/lib/models";

/**
 * "Add / Edit Responsibility" modal. Mirrors iOS
 * `Views/Responsibilities/AddResponsibilityView.swift` create + edit
 * + delete flow and Android `AddResponsibilitySheet.kt`.
 *
 * When `existing` is supplied the modal opens in edit mode: fields
 * pre-populate, the submit action becomes `updateDoc`, and a
 * destructive Delete button appears at the bottom.
 *
 * The Firestore body is assembled by the pure helper
 * `buildResponsibilityDoc`, which is unit-tested independently.
 */

interface CreateResponsibilityModalProps {
  isOpen: boolean;
  onClose: () => void;
  householdId: string;
  createdBy: string;
  members: MemberProfile[];
  /**
   * Pre-existing responsibility — when present, the modal opens in
   * edit mode. Optional so callers can use the same component for
   * both create and edit surfaces.
   */
  existing?: (Responsibility & { uid: string }) | null;
  onError?: (message: string) => void;
}

const RECURRENCE_OPTIONS: { value: RecurrenceValue; key: string }[] = [
  { value: "none", key: "responsibilities.create.recurrence.none" },
  { value: "daily", key: "responsibilities.create.recurrence.daily" },
  { value: "weekdays", key: "responsibilities.create.recurrence.weekdays" },
  { value: "weekly", key: "responsibilities.create.recurrence.weekly" },
  { value: "monthly", key: "responsibilities.create.recurrence.monthly" },
];

const DAY_OF_WEEK_KEYS: { value: number; key: string }[] = [
  { value: 0, key: "responsibilities.create.day.sun" },
  { value: 1, key: "responsibilities.create.day.mon" },
  { value: 2, key: "responsibilities.create.day.tue" },
  { value: 3, key: "responsibilities.create.day.wed" },
  { value: 4, key: "responsibilities.create.day.thu" },
  { value: 5, key: "responsibilities.create.day.fri" },
  { value: 6, key: "responsibilities.create.day.sat" },
];

export function CreateResponsibilityModal({
  isOpen,
  onClose,
  householdId,
  createdBy,
  members,
  existing,
  onError,
}: CreateResponsibilityModalProps) {
  const t = useTranslations();
  const isEditing = Boolean(existing?.uid);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [recurrence, setRecurrence] = useState<RecurrenceValue>("daily");
  const [dayOfWeek, setDayOfWeek] = useState<number>(1);
  const [effectiveTime, setEffectiveTime] = useState("");
  const [actionLabel, setActionLabel] = useState("");
  const [assignedMemberId, setAssignedMemberId] = useState<string>("");
  const [selectedMemberIds, setSelectedMemberIds] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const memberLookup = useMemo(() => {
    const map: Record<string, MemberProfile> = {};
    for (const m of members) {
      if (m.uid) map[m.uid] = m;
    }
    return map;
  }, [members]);

  // Hydrate fields when opening in edit mode (or reset to blank on
  // create). The dependency on `isOpen` ensures we re-hydrate every
  // time the same modal is reopened with a different `existing`.
  useEffect(() => {
    if (!isOpen) return;
    if (existing) {
      setTitle(existing.title ?? "");
      setDescription(existing.description ?? "");
      const rec = (existing.recurrence as RecurrenceValue) ?? "daily";
      setRecurrence(rec);
      setDayOfWeek(
        typeof existing.dayOfWeek === "number" ? existing.dayOfWeek : 1,
      );
      setEffectiveTime(existing.scheduledTime ?? "");
      setActionLabel(existing.actionLabel ?? "");
      setSelectedMemberIds(new Set(existing.linkedEntityIds ?? []));
      setAssignedMemberId("");
    } else {
      setTitle("");
      setDescription("");
      setRecurrence("daily");
      setDayOfWeek(1);
      setEffectiveTime("");
      setActionLabel("");
      setAssignedMemberId("");
      setSelectedMemberIds(new Set());
    }
    setShowDeleteConfirm(false);
    setSubmitting(false);
  }, [isOpen, existing]);

  function close() {
    setSubmitting(false);
    setShowDeleteConfirm(false);
    onClose();
  }

  function toggleMember(id: string) {
    setSelectedMemberIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting) return;

    const docBody = buildResponsibilityDoc({
      title,
      description,
      recurrence,
      dayOfWeek,
      effectiveTime,
      actionLabel,
      linkedEntityIds: Array.from(selectedMemberIds),
      householdId,
      createdBy,
      memberLookup,
    });

    if (!docBody) return;

    setSubmitting(true);
    try {
      if (isEditing && existing?.uid) {
        const ref = doc(db, `households/${householdId}/responsibilities`, existing.uid);
        // Strip created_at on update — that's an immutable audit
        // field; the buildResponsibilityDoc helper sets a fresh
        // `Timestamp.now()` which we don't want to overwrite.
        const { created_at: _ignored, ...updates } = docBody;
        void _ignored;
        // Ensure removed-on-edit fields actually clear server-side.
        await updateDoc(ref, {
          ...updates,
          actionLabel: docBody.actionLabel ?? null,
          scheduledTime: docBody.scheduledTime ?? null,
          dayOfWeek: docBody.dayOfWeek ?? null,
          description: docBody.description ?? null,
          linkedEntityIds: docBody.linkedEntityIds ?? [],
          linkedEntityId: docBody.linkedEntityId ?? null,
          linkedEntityType: docBody.linkedEntityType ?? null,
        });
      } else {
        const respRef = await addDoc(
          collection(db, `households/${householdId}/responsibilities`),
          docBody,
        );

        // If an "Assign to" was selected, drop a claim immediately on
        // their behalf. Mirrors iOS AddResponsibilityView:329-343.
        if (assignedMemberId) {
          await addDoc(
            collection(db, `member_profiles/${assignedMemberId}/responsibility_claims`),
            {
              responsibility_uid: respRef.id,
              household_id: householdId,
              claimed_by: assignedMemberId,
              claimed_at: Timestamp.now(),
              status: "claimed",
            },
          );
        }
      }
      close();
    } catch (err) {
      console.error("Failed to save responsibility:", err);
      onError?.(t("responsibilities.create.error"));
      setSubmitting(false);
    }
  }

  async function handleDelete() {
    if (!isEditing || !existing?.uid) return;
    setSubmitting(true);
    try {
      const ref = doc(db, `households/${householdId}/responsibilities`, existing.uid);
      await deleteDoc(ref);
      close();
    } catch (err) {
      console.error("Failed to delete responsibility:", err);
      onError?.(t("responsibilities.create.error"));
      setSubmitting(false);
    }
  }

  if (!isOpen) return null;

  const titleTrimmed = title.trim();
  const canSubmit = titleTrimmed.length > 0 && !submitting;

  return (
    <div className="fixed inset-0 z-40 flex items-end sm:items-center justify-center">
      <div
        className="absolute inset-0 bg-black/40"
        onClick={() => {
          if (!submitting) close();
        }}
      />
      <div className="relative z-50 w-full max-w-md mx-4 mb-4 sm:mb-0 bg-white rounded-2xl shadow-xl overflow-hidden text-gray-900 max-h-[90vh] flex flex-col">
        <div className="p-4 border-b border-gray-100 flex-shrink-0">
          <h3 className="font-semibold text-center">
            {isEditing
              ? t("responsibilities.edit.title")
              : t("responsibilities.create.title")}
          </h3>
        </div>

        <form
          onSubmit={handleSubmit}
          className="p-4 space-y-4 overflow-y-auto"
        >
          {/* Title (required) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("responsibilities.create.titleField.label")}
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              maxLength={100}
              placeholder={t("responsibilities.create.titleField.placeholder")}
              className="w-full rounded-lg border border-gray-200 bg-white text-gray-900 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[color:var(--accent-color)]"
            />
          </div>

          {/* Description (optional) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("responsibilities.create.descriptionField.label")}
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder={t("responsibilities.create.descriptionField.placeholder")}
              className="w-full rounded-lg border border-gray-200 bg-white text-gray-900 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[color:var(--accent-color)] resize-none"
            />
          </div>

          {/* Action label (optional) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("responsibilities.create.actionLabelField.label")}
            </label>
            <input
              type="text"
              value={actionLabel}
              onChange={(e) => setActionLabel(e.target.value)}
              maxLength={32}
              placeholder={t("responsibilities.create.actionLabelField.placeholder")}
              className="w-full rounded-lg border border-gray-200 bg-white text-gray-900 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[color:var(--accent-color)]"
            />
          </div>

          {/* Recurrence (pill picker) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("responsibilities.create.recurrenceField.label")}
            </label>
            <div
              className="flex flex-wrap gap-2"
              role="radiogroup"
              aria-label={t("responsibilities.create.recurrenceField.label")}
            >
              {RECURRENCE_OPTIONS.map((opt) => {
                const active = recurrence === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() => setRecurrence(opt.value)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors border ${
                      active
                        ? "text-white border-transparent"
                        : "text-gray-700 border-gray-200 hover:bg-gray-50"
                    }`}
                    style={
                      active
                        ? { backgroundColor: "var(--accent-color, #00C7BE)" }
                        : undefined
                    }
                  >
                    {t(opt.key)}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Day of week (only when recurrence is weekly) */}
          {recurrence === "weekly" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("responsibilities.create.dayOfWeekField.label")}
              </label>
              <select
                value={dayOfWeek}
                onChange={(e) => setDayOfWeek(parseInt(e.target.value, 10))}
                className="w-full rounded-lg border border-gray-200 bg-white text-gray-900 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[color:var(--accent-color)]"
              >
                {DAY_OF_WEEK_KEYS.map((d) => (
                  <option key={d.value} value={d.value}>
                    {t(d.key)}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Effective time (optional) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("responsibilities.create.timeField.label")}
            </label>
            <input
              type="time"
              value={effectiveTime}
              onChange={(e) => setEffectiveTime(e.target.value)}
              className="w-full rounded-lg border border-gray-200 bg-white text-gray-900 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[color:var(--accent-color)]"
            />
          </div>

          {/* Assign-to (single member, create-only) — drops a
              ResponsibilityClaim immediately on save. */}
          {!isEditing && members.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("responsibilities.create.assignToField.label")}
              </label>
              <select
                value={assignedMemberId}
                onChange={(e) => setAssignedMemberId(e.target.value)}
                className="w-full rounded-lg border border-gray-200 bg-white text-gray-900 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[color:var(--accent-color)]"
              >
                <option value="">
                  {t("responsibilities.create.assignToField.unassigned")}
                </option>
                {members.map((m) =>
                  m.uid ? (
                    <option key={m.uid} value={m.uid}>
                      {m.nickname ?? m.name ?? m.uid}
                    </option>
                  ) : null,
                )}
              </select>
            </div>
          )}

          {/* Linked members (multi-select) */}
          {members.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("responsibilities.create.whoField.label")}
              </label>
              <div className="space-y-1 rounded-lg border border-gray-200 p-2">
                {members.map((member) => {
                  const id = member.uid;
                  if (!id) return null;
                  const checked = selectedMemberIds.has(id);
                  return (
                    <label
                      key={id}
                      className="flex items-center gap-3 px-2 py-1.5 rounded-lg cursor-pointer hover:bg-gray-50"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleMember(id)}
                        className="w-4 h-4 rounded border-gray-300 text-[color:var(--accent-color)] focus:ring-[color:var(--accent-color)]"
                      />
                      <Avatar
                        src={member.profile_url}
                        alt={member.nickname ?? member.name ?? ""}
                        size={24}
                      />
                      <span className="text-sm text-gray-900">
                        {member.nickname ?? member.name ?? id}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={close}
              disabled={submitting}
              className="flex-1 py-2 rounded-lg text-sm font-medium border border-gray-200 hover:bg-gray-50 transition-colors disabled:opacity-40"
            >
              {t("responsibilities.actions.cancel")}
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="flex-1 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-40 transition-opacity"
              style={{ backgroundColor: "var(--accent-color, #00C7BE)" }}
            >
              {submitting
                ? t("responsibilities.create.submitting")
                : isEditing
                  ? t("responsibilities.edit.save")
                  : t("responsibilities.create.submit")}
            </button>
          </div>

          {isEditing && (
            <button
              type="button"
              onClick={() => setShowDeleteConfirm(true)}
              disabled={submitting}
              className="w-full py-2 rounded-lg text-sm font-semibold text-red-600 hover:bg-red-50 transition-colors disabled:opacity-40"
            >
              {t("responsibilities.edit.delete")}
            </button>
          )}
        </form>
      </div>

      {showDeleteConfirm && existing?.uid && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => !submitting && setShowDeleteConfirm(false)}
          />
          <div className="relative w-full max-w-sm rounded-2xl bg-white p-5 shadow-xl text-gray-900">
            <h3 className="font-bold text-base">
              {t("responsibilities.deleteConfirm.title", { title: existing.title ?? "" })}
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              {t("responsibilities.deleteConfirm.body")}
            </p>
            <div className="mt-4 flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={submitting}
                className="px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-gray-100 disabled:opacity-50"
              >
                {t("responsibilities.actions.cancel")}
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={submitting}
                className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
              >
                {submitting
                  ? t("responsibilities.deleteConfirm.deleting")
                  : t("responsibilities.deleteConfirm.confirm")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
